import React from "react"
import {useEffect, useState} from "react";
import Grid from "@mui/material/Grid2"
import {Box, FormControl, FormControlLabel, Radio, RadioGroup, TextField, Typography} from '@mui/material';
import {useDispatch, useSelector} from "react-redux"
import {RootState} from "../../../reducers"
import lidImage from "../../../image/vaultComponentQuantity/lid.jpg"
import vaultImage from "../../../image/vaultComponentQuantity/vault.jpg";
import stockVaultImage from "../../../image/vaultComponentQuantity/stockVault.jpg";
import intermediateCeilingImage from "../../../image/vaultComponentQuantity/intermediateCeiling.jpg";
import floorImage from "../../../image/vaultComponentQuantity/floor.png";
import stockFloorImage from "../../../image/vaultComponentQuantity/stockFloor.jpg";
import {canHaveMultipleLids, getLidSizes} from "../../domain/lid";
import {getVaultSize} from "../../domain/vault";
import {getFloorSize} from "../../domain/floor";
import {getIntermediateCeilingSize} from "../../domain/intermediateCeiling";
import {
    setVaultElementQuantity,
    setIncludeWireRopeLoops,
    VaultElementType, VaultElement
} from "../../../features/project/vaultElementSlice";
import {
    selectVaultElementQuantity,
    selectIncludeWireRopeLoops,
    selectVaultElements
} from "../../../features/project/vaultElementSelectors";
import {computeDerived, prunePlan} from "../../../actions/planApiHelpers";
import {apiUrls} from "../../../constants";
import {updateVaultElements} from "../../../features/project/updateVaultElementsThunk";

const TitleItem: React.FC = () => (
    <>
        <Grid size={12}>
            <Typography component="h1" variant="h4">
                Menge
            </Typography>
            <hr/>
        </Grid>
    </>
);

type ConfiguredProduct = {
    title: string;
    image: any;
    dimensions: string;
    weight: string | number;
    partNumber: string | null;
    quantity: number;
    setQuantity: (value: number) => void;
    includeWireLoops: boolean;
    setWireLoops: (value: boolean) => void;
};

interface ProductProps {
    product: ConfiguredProduct;
}

const Product: React.FC<ProductProps> = ({product}) => {
    const {
        title,
        image,
        dimensions,
        weight,
        partNumber,
        quantity,
        setQuantity,
        includeWireLoops,
        setWireLoops
    } = product;
    const radioName = `${title.replace(/\s+/g, '-')}-loop`;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                component="img"
                width={438}
                height={207}
                src={image}
                alt="Product"
                sx={{mb: 2}} // theme spacing
            />
            <Box display="flex" gap={2} alignItems="center">
                <TextField
                    type="number"
                    size="small"
                    value={quantity}
                    onChange={event => {
                        const value = parseInt(event.target.value, 10);
                        if (!isNaN(value)) {
                            setQuantity(value);
                        }
                    }}
                    sx={{width: 60}}
                    slotProps={{htmlInput: {min: 0}}}
                    onFocus={event => event.target.select()}
                />
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {title}
                    </Typography>
                    <Typography variant="body2">
                        {dimensions}, {weight} kg{partNumber ? `, ${partNumber}` : ''}
                    </Typography>
                </Box>
            </Box>
            <Box>
                <FormControl>
                    <RadioGroup
                        row
                        name={radioName}
                        value={includeWireLoops ? "with" : "none"}
                        onChange={event => {
                            const includeWireLoops = event.target.value === "with"
                            setWireLoops(includeWireLoops)
                        }}
                    >
                        <FormControlLabel
                            value="none"
                            control={<Radio/>}
                            label="Ohne Seilschlaufen"
                            sx={{'& .MuiFormControlLabel-label': {fontSize: 12}}}
                        />
                        <FormControlLabel
                            value="with"
                            control={<Radio/>}
                            label={"Mit Seilschlaufen"}
                            sx={{'& .MuiFormControlLabel-label': {fontSize: 12}}}
                        />
                    </RadioGroup>
                </FormControl>
            </Box>
        </Box>
    );
};

type VaultElementInfo = {
    weight: number;
    partNumber?: string;
    manholeIds?: string[];
};

type VaultElementInfos = {
    vault?: VaultElementInfo;
    lids?: VaultElementInfo[]; // multiple lid infos
    intermediateCeiling?: VaultElementInfo;
    floor?: VaultElementInfo;
};

export const VaultElementsSection: React.FC = () => {
    const dispatch = useDispatch<any>();
    const plan = useSelector((state: RootState) => state.plan);
    const [infos, setInfos] = useState<VaultElementInfos>({});

    useEffect(() => {
        dispatch(updateVaultElements());
    }, [plan, dispatch])

    useEffect(() => {
        const fetchInfos = async () => {
            try {
                const derived = computeDerived(plan)
                const response = await fetch(apiUrls.vaultElementInfoEndpoint,
                    {
                        method: "POST",
                        headers: {"Content-Type": "application/json"},
                        body: JSON.stringify({
                            plan: prunePlan(plan),
                            derived: derived
                        })
                    });
                const data = await response.json();
                if (data.status === "success") {
                    setInfos({
                        vault: data.vault
                            ? {weight: data.vault.weight ?? 0, partNumber: data.vault.partNumber}
                            : undefined,
                        lids: Array.isArray(data.lids)
                            ? data.lids.map((l: any) => ({
                                weight: l.weight,
                                partNumber: l.partNumber,
                                manholeIds: l.manholeIds ?? []
                            }))
                            : undefined,
                        intermediateCeiling: data.intermediateCeiling
                            ? {
                                weight: data.intermediateCeiling.weight ?? 0,
                                partNumber: data.intermediateCeiling.partNumber
                            }
                            : undefined,
                        floor: data.floor
                            ? {weight: data.floor.weight ?? 0, partNumber: data.floor.partNumber}
                            : undefined
                    });
                }
            } catch (e) {
                console.error("Error fetching vault element weights:", e);
            }
        };
        if (plan) void fetchInfos();
    }, [plan]);

    const vaultElements = useSelector(selectVaultElements);
    const vaultQuantity = useSelector(selectVaultElementQuantity(VaultElementType.Vault, null));
    const vaultWireLoops = useSelector(selectIncludeWireRopeLoops(VaultElementType.Vault, null));
    const intermediateCeilingQuantity = useSelector(
        selectVaultElementQuantity(VaultElementType.IntermediateCeiling, null)
    );
    const intermediateCeilingWireLoops = useSelector(
        selectIncludeWireRopeLoops(VaultElementType.IntermediateCeiling, null)
    );
    const floorQuantity = useSelector(selectVaultElementQuantity(VaultElementType.Floor, null));
    const floorWireLoops = useSelector(selectIncludeWireRopeLoops(VaultElementType.Floor, null));
    const products = [
        {title: "Abdeckung, gem. Zeichnung", image: lidImage},
        {title: "Kabelschacht, gem. Zeichnung", image: vaultImage},
        {title: "Standard Kabelschacht, gem. Zeichnung", image: stockVaultImage},
        {title: "Zwischendecke gem. Zeichnung", image: intermediateCeilingImage},
        {title: "Boden gem. Zeichnung", image: floorImage},
        {title: "Standard Boden gem. Zeichnung", image: stockFloorImage},
    ];

    type SizeDims = { length: number; width: number; height: number, manholeIds: string[] };

    function getUniqueSizes<T extends SizeDims>(
        sizes: T[]
    ): Array<SizeDims & { count: number }> {
        const map = new Map<string, {
            length: number;
            width: number;
            height: number;
            manholeIds: string[];
            count: number
        }>();

        for (const {length, width, height, manholeIds} of sizes) {
            const l = Math.round(Math.max(length, width))
            const w = Math.round(Math.min(length, width))
            const h = Math.round(height)
            const key = `${l}|${w}|${h}`;
            const entry = map.get(key);
            if (entry) {
                entry.count++;
            } else {
                map.set(key, {length: l, width: w, height: h, manholeIds, count: 1});
            }
        }

        // sort by length, then width, then height (all descending)
        return Array.from(map.values()).sort(
            (a, b) =>
                b.length - a.length ||
                b.width - a.width ||
                b.height - a.height
        );
    }

    const toLidProducts = (plan: any): ConfiguredProduct[] => {
        const lidSizes = getLidSizes(plan)
        const uniqueSizes = getUniqueSizes(lidSizes);
        return uniqueSizes.map(size => toLidProduct(size));
    };

    const findLidInfoForSize = (size: SizeDims): VaultElementInfo | undefined => {
        const lids = infos.lids ?? [];
        // prefer any lid that shares a manhole id with the size
        const match = lids.find(lid => lid.manholeIds?.some(id => size.manholeIds.includes(id)));
        return match ?? lids[0];
    };

    const toLidProduct = (size: SizeDims): ConfiguredProduct => {
        const product = products.find(product => product.title.startsWith("Abdeckung"));

        if (!product) {
            throw new Error("Product not found for lid");
        }
        const dimensions = `${size.length} x ${size.width} x ${size.height} cm`;
        const vaultElementInfo = findLidInfoForSize(size);
        const weight = vaultElementInfo?.weight ?? 0;
        const partNumber = vaultElementInfo?.partNumber ?? null;
        const manholeId = canHaveMultipleLids(plan) ? size.manholeIds[0] ?? null : null;
        const vaultElement = vaultElements.find(
            (e: VaultElement) => e.elementType === VaultElementType.Lid && e.id === manholeId
        );
        const lidQuantity = vaultElement?.quantity ?? 0;
        const lidWireLoops = vaultElement?.includeWireRopeLoops;

        return {
            ...product,
            dimensions,
            weight,
            partNumber,
            quantity: lidQuantity,
            setQuantity: (value: number) =>
                dispatch(setVaultElementQuantity({elementType: VaultElementType.Lid, id: manholeId, quantity: value})),
            includeWireLoops: lidWireLoops,
            setWireLoops: (value: boolean) => dispatch(setIncludeWireRopeLoops(
                {elementType: VaultElementType.Lid, id: manholeId, includeWireRopeLoops: value}
            ))
        };
    };

    const toVaultProduct = (plan: any): ConfiguredProduct => {
        const product = plan.vault?.isStockVault
            ? products.find(product => product.title.startsWith("Standard Kabelschacht"))
            : products.find(product => product.title.startsWith("Kabelschacht"));

        if (!product) {
            throw new Error("Product not found for vault");
        }
        const size = getVaultSize(plan, false);
        const dimensions = `${size.length} x ${size.width} x ${size.height} cm`;
        const weight = infos.vault?.weight ?? 0;
        const partNumber = infos.vault?.partNumber ?? null;

        return {
            ...product,
            dimensions,
            weight,
            partNumber,
            quantity: vaultQuantity,
            setQuantity: (value: number) =>
                dispatch(setVaultElementQuantity({elementType: VaultElementType.Vault, id: null, quantity: value})),
            includeWireLoops: vaultWireLoops,
            setWireLoops: (value: boolean) =>
                dispatch(setIncludeWireRopeLoops({
                    elementType: VaultElementType.Vault,
                    id: null,
                    includeWireRopeLoops: value
                }))
        };
    };

    const toIntermediateCeilingProduct = (plan: any): ConfiguredProduct => {
        const product = products.find(product => product.title.startsWith("Zwischendecke"));

        if (!product) {
            throw new Error("Product not found for intermediate ceiling");
        }
        const size = getIntermediateCeilingSize(plan);
        const dimensions = `${size.length} x ${size.width} x ${size.height} cm`;
        const weight = infos.intermediateCeiling?.weight ?? 0;
        const partNumber = infos.intermediateCeiling?.partNumber ?? null;

        return {
            ...product,
            dimensions,
            weight,
            partNumber,
            quantity: intermediateCeilingQuantity,
            setQuantity: (value: number) =>
                dispatch(setVaultElementQuantity({
                    elementType: VaultElementType.IntermediateCeiling,
                    id: null,
                    quantity: value
                })),
            includeWireLoops: intermediateCeilingWireLoops,
            setWireLoops: (value: boolean) =>
                dispatch(setIncludeWireRopeLoops({
                    elementType: VaultElementType.IntermediateCeiling,
                    id: null,
                    includeWireRopeLoops: value
                }))
        };
    };

    const toFloorProduct = (plan: any): ConfiguredProduct => {
        const product = plan.floor?.isStockFloor
            ? products.find(product => product.title.startsWith("Standard Boden"))
            : products.find(product => product.title.startsWith("Boden"));

        if (!product) {
            throw new Error("Product not found for floor");
        }
        const size = getFloorSize(plan);
        const dimensions = `${size.length} x ${size.width} x ${size.height} cm`;
        const weight = infos.floor?.weight ?? 0;
        const partNumber = infos.floor?.partNumber ?? null;

        return {
            ...product,
            dimensions,
            weight,
            partNumber,
            quantity: floorQuantity,
            setQuantity: (value: number) =>
                dispatch(setVaultElementQuantity({elementType: VaultElementType.Floor, id: null, quantity: value})),
            includeWireLoops: floorWireLoops,
            setWireLoops: (value: boolean) =>
                dispatch(setIncludeWireRopeLoops({
                    elementType: VaultElementType.Floor,
                    id: null,
                    includeWireRopeLoops: value
                }))
        };
    };

    const configuredProducts =
        [
            ...(!!plan.lid ? toLidProducts(plan) : []),
            ...(!!plan.vault ? [toVaultProduct(plan)] : []),
            ...(!!plan.intermediateCeiling ? [toIntermediateCeilingProduct(plan)] : []),
            ...(!!plan.floor ? [toFloorProduct(plan)] : []),
        ];

    return (
        <Grid container size={12} spacing={3}>
            <TitleItem/>
            <Box display="flex" flexWrap="wrap" gap={3} rowGap={2}>
                {configuredProducts.map((product) => (
                    <Product
                        key={product.title + '-' + product.dimensions}
                        product={product}
                    />
                ))}
            </Box>
        </Grid>
    );
};
